import { useEffect, useMemo, useState } from 'react';

import {
  buildOpeningExperiencePreviewData,
  OpeningExperiencePlayer,
} from '@tov-plus/ui';

import { useGuestAccess } from '../contexts/GuestAccessContext';

function buildStorageKey(occasionSlug: string, guestToken: string) {
  return `tov-plus.guests.opening-experience:${occasionSlug}:${guestToken}`;
}

export function GuestOpeningExperienceGate({
  children,
}: {
  children: React.ReactNode;
}) {
  const access = useGuestAccess();
  const [dismissed, setDismissed] = useState(false);

  const shouldShow = useMemo(() => {
    if (access.status !== 'ready' || !access.experience.openingExperience) {
      return false;
    }

    if (access.experience.openingExperience.type === 'none') {
      return false;
    }

    if (!access.experience.openingExperience.showOnFirstVisitOnly) {
      return !dismissed;
    }

    return !dismissed;
  }, [access, dismissed]);

  useEffect(() => {
    if (access.status !== 'ready' || !access.experience.openingExperience) {
      setDismissed(false);
      return;
    }

    const storageKey = buildStorageKey(access.occasionSlug, access.guestToken);
    const alreadyViewed =
      access.experience.openingExperience.showOnFirstVisitOnly &&
      window.localStorage.getItem(storageKey) === 'seen';
    setDismissed(alreadyViewed);
  }, [access]);

  if (access.status !== 'ready' || !access.experience.openingExperience) {
    return <>{children}</>;
  }

  const previewData = buildOpeningExperiencePreviewData({
    title:
      access.experience.guestDisplayName ||
      access.experience.occasion.displayName,
    dateLabel: access.experience.events[0]?.startsAt
      ? new Date(access.experience.events[0].startsAt).toLocaleDateString()
      : 'September 15, 2027',
    locationLabel:
      access.experience.events[0]?.locationName ||
      access.experience.occasion.displayName,
    themeLabel:
      access.experience.designTheme?.themeName ||
      access.experience.occasion.displayName,
  });

  return (
    <>
      {children}
      {shouldShow ? (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 90,
            padding: '1rem',
            background: 'rgba(20, 18, 15, 0.34)',
            backdropFilter: 'blur(6px)',
          }}
        >
          <OpeningExperiencePlayer
            allowSkip={access.experience.openingExperience.allowSkip}
            configuration={access.experience.openingExperience.configuration}
            experienceType={access.experience.openingExperience.type}
            invitationData={previewData}
            onComplete={() => {
              const storageKey = buildStorageKey(
                access.occasionSlug,
                access.guestToken,
              );
              if (access.experience.openingExperience?.showOnFirstVisitOnly) {
                window.localStorage.setItem(storageKey, 'seen');
              }
              setDismissed(true);
            }}
            onSkip={() => {
              const storageKey = buildStorageKey(
                access.occasionSlug,
                access.guestToken,
              );
              if (access.experience.openingExperience?.showOnFirstVisitOnly) {
                window.localStorage.setItem(storageKey, 'seen');
              }
            }}
          />
        </div>
      ) : null}
    </>
  );
}
